// External dependencies.
import React, { useCallback, useState } from "react";
import { FormattedMessage, FormattedDate, defineMessages, injectIntl, intlShape } from "react-intl";
import Cookies from "js-cookie";
import { XMarkIcon, ArrowRightIcon } from "@heroicons/react/20/solid";
import { Card, Button, Link, Table } from "@yoast/ui-library";

// Internal dependencies.
import useUpcomingRenewals from "../../hooks/useUpcomingRenewals";
import groupBy from "../../functions/groupBy";

import * as styles from "./styles.module.scss";

const HIDE_COOKIE = "hideRenewalNotification";

const messages = defineMessages( {
	header: {
		id: "renewal.notification.header",
		defaultMessage: "One or more subscriptions are about to expire!",
	},
	description: {
		id: "renewal.notification.description",
		defaultMessage: "When they do, you will no longer receive (security) updates or support!",
	},
	close: {
		id: "renewal.notification.close",
		defaultMessage: "close",
	},
	linkMessage: {
		id: "renewal.notification.link.message",
		defaultMessage: "Renew now",
	},
	productHeader: {
		id: "headers.product",
		defaultMessage: "Subscription number",
	},
	expiryHeader: {
		id: "headers.expiry",
		defaultMessage: "Expires on",
	},
	productsHeader: {
		id: "headers.products",
		defaultMessage: "Product(s)",
	},
} );

/**
 * Renewal notification message.
 *
 * Warns the customer about subscriptions they have to renew by hand before they lapse. The rows come
 * from a purpose-scoped read model, already filtered to what needs renewing and ordered soonest first,
 * so this component only groups and renders them.
 *
 * The "Buy subscription" branch this used to carry is gone: it keyed off an expiry more than a month in
 * the *past*, which the rows here cannot have, since a lapsed subscription is no longer active. Buying
 * a replacement remains available on the subscriptions page, where the status check is done properly.
 *
 * @param {object} props Properties of the component.
 * @returns {ReactElement|null} The notification, or null when there is nothing to warn about.
 */
const RenewalNotification = ( { intl } ) => {
	const { renewals } = useUpcomingRenewals();
	// Read once on mount rather than watched: the cookie only changes from the dismissal below.
	const [ isHidden, setIsHidden ] = useState( () => Boolean( Cookies.get( HIDE_COOKIE ) ) );

	const onCrossClick = useCallback( () => {
		Cookies.set( HIDE_COOKIE, "true", { expires: 7 } );
		setIsHidden( true );
	}, [] );

	if ( isHidden || renewals.length < 1 ) {
		return null;
	}

	// Several subscriptions can share one subscription number; the banner shows them as one row with a
	// line per product. Grouping stays here rather than in SQL so the projection remains a flat select.
	const groupedRenewals = Object.values( groupBy( renewals, "subscriptionNumber" ) );

	return (
		<Card className={ styles.messageContainer }>
			<XMarkIcon
				className={ styles.closeButton }
				onClick={ onCrossClick }
				aria-label={ intl.formatMessage( messages.close ) }
			/>
			<div className={ styles.imageTextContainer }>
				<div className={ styles.inlineBlock }>
					<p className={ styles.title }><FormattedMessage { ...messages.header } /></p>
					<FormattedMessage { ...messages.description } />
				</div>
			</div>
			<div className={ styles.tableContainer }>
				<Table className={ styles.table }>
					<Table.Head>
						<Table.Row className={ styles.header }>
							<Table.Header>{ intl.formatMessage( messages.productHeader ) }</Table.Header>
							<Table.Header>{ intl.formatMessage( messages.productsHeader ) }</Table.Header>
							<Table.Header>{ intl.formatMessage( messages.expiryHeader ) }</Table.Header>
							<Table.Header>{ "" }</Table.Header>
						</Table.Row>
					</Table.Head>
					<Table.Body>
						{ groupedRenewals.map( group => {
							// Every row in a group shares the subscription number and, being one subscription
							// number, the same expiry and renewal link.
							const first = group[ 0 ];

							return (
								<Table.Row key={ first.subscriptionNumber } className={ styles.table }>
									<Table.Cell>
										<span className={ styles.cellLabel }>
											{ intl.formatMessage( messages.productHeader ) }
										</span>
										#{ first.subscriptionNumber }
									</Table.Cell>
									<Table.Cell>
										{ group.map( ( item, index ) => (
											<p
												className={ styles.renewalItem }
												key={ `${ item.subscriptionNumber }-${ index }` }
											>
												<span>{ item.limit } x { item.subscriptionName }</span>
											</p>
										) ) }
									</Table.Cell>
									<Table.Cell>
										<span className={ styles.cellLabel }>
											{ intl.formatMessage( messages.expiryHeader ) }
										</span>
										<FormattedDate
											value={ first.expiryDate }
											year="numeric"
											month="long"
											day="2-digit"
										/>
									</Table.Cell>
									<Table.Cell className={ styles.upsell }>
										{ /*
										  * renewalUrl is null for a subscription with no subscription number, which the
										  * column permits. Rendering the button anyway would give it a dead link, so the
										  * row keeps its expiry information and simply offers nothing to click.
										  */ }
										{ first.renewalUrl
											? <Link href={ first.renewalUrl } target="_blank">
												{ /* The class is a GTM selector, so it stays spelled exactly as it was. */ }
												<Button variant="upsell" className="renew_now_button_cta">
													<FormattedMessage { ...messages.linkMessage } />
													<ArrowRightIcon />
												</Button>
											</Link>
											: "" }
									</Table.Cell>
								</Table.Row>
							);
						} ) }
					</Table.Body>
				</Table>
			</div>
		</Card>
	);
};

RenewalNotification.propTypes = {
	intl: intlShape.isRequired,
};

export default injectIntl( RenewalNotification );
